"use client";

import React, { useEffect, useState } from "react";
import styles from "./EducationInstructors.module.scss";
import { Builder } from "@builder.io/react";
import { initBuilder } from "@/lib/builder/builder-utils";
import useIsPreviewingBuilder from "@/hooks/useIsPreviewingBuilder";
import Button from "../UI/Button/Button";
import Modal from "../Modal/Modal";
import { useI18n } from "@/i18n/client";
import Markdown from "../UI/Markdown/Markdown";
import { builder } from "@builder.io/sdk";
import { BUILDER_CONFIG } from "@/lib/builder/builder.config";

type RepresentativeData = {
	imageAltText: string;
	name: string;
	image: string;
	title: string;
	email: string;
	serviceCentres: {
		serviceCentre: string;
	}[];
	bioDescription: string;
};

type RepresentativeMeta = {
	kind: string;
	lastPreviewUrl: string;
};

type RepresentativeValue = {
	query: any[];
	folders: any[];
	createdDate: number;
	id: string;
	name: string;
	modelId: string;
	published: string;
	meta: RepresentativeMeta;
	data: RepresentativeData;
	variations: Record<string, unknown>;
	lastUpdated: number;
	firstPublished: number;
	testRatio: number;
	createdBy: string;
	lastUpdatedBy: string;
	"@originModelId": string;
	rev: string;
};

type SelectRepresentative = {
	"@type": "@builder.io/core:Reference";
	id: string;
	model: string;
	value: RepresentativeValue;
};

type RepresentativeItem = {
	selectRepresentative: SelectRepresentative;
};

interface EducationInstructorsProps {
	representatives: RepresentativeItem[];
}

const EducationInstructors: React.FC<EducationInstructorsProps> = ({
	representatives = [],
}) => {
	initBuilder();

	const { isEditingBuilder } = useIsPreviewingBuilder();
	const [representativesData, setRepresentativesData] = useState<
		RepresentativeValue[]
	>([]);

	const [representativeUser, setRepresentativeUser] =
		useState<RepresentativeValue | null>(null);
	const t = useI18n();

	useEffect(() => {
		if (!isEditingBuilder) {
			setRepresentativesData(
				representatives?.map(
					(representative) => representative?.selectRepresentative?.value,
				),
			);
		}
		if (representatives?.length) {
			builder
				.getAll("representatives", {
					apiKey: BUILDER_CONFIG.publicKey,
					query: {
						id: {
							$in: representatives?.map((rep) => rep.selectRepresentative?.id),
						},
					},
				})
				.then((res) => {
					const sorted = [...res].sort((a, b) => {
						const indexA = representatives.findIndex(
							(rep) => rep.selectRepresentative?.id === a.id,
						);

						const indexB = representatives.findIndex(
							(rep) => rep.selectRepresentative?.id === b.id,
						);

						if (indexA < indexB) return -1;
						if (indexA > indexB) return 1;
						return 0;
					});
					setRepresentativesData(sorted as RepresentativeValue[]);
				});
		}
	}, [isEditingBuilder, representatives.length]);
	return (
		<section className={styles.section}>
			{representativesData &&
				representativesData?.map((rep) => (
					<div key={rep.id} className={styles.card}>
						<img
							src={`${rep.data?.image}?format=webp`}
							alt={rep.data?.imageAltText}
						/>
						<div className={styles.name}>{rep.data.name}</div>
						<Button
							className={styles.viewButton}
							onClick={() => setRepresentativeUser(rep)}
							variant='secondary'
							as='button'
						>
							{t("education.viewBio")}
						</Button>
					</div>
				))}
			<Modal
				ariaLabel={t("education.educationalInstructorModal")}
				contentId='education-instructor-modal'
				isOpen={!!representativeUser}
				contentClassName={styles.modalContent}
				toggleModal={() => setRepresentativeUser(null)}
			>
				<div>
					{representativeUser?.data.bioDescription ? (
						<Markdown
							className={styles.markdown}
							content={representativeUser?.data.bioDescription}
						/>
					) : (
						t("education.notAvailable")
					)}
				</div>
			</Modal>
		</section>
	);
};

export default EducationInstructors;

Builder.registerComponent(EducationInstructors, {
	name: "EducationInstructors",
	inputs: [
		{
			name: "representatives",
			type: "list",
			subFields: [
				{
					name: "selectRepresentative",
					type: "reference",
					model: "representatives",
				},
			],
		},
	],
});
